/**
 * PressKit — public marketing page.
 *
 * Surfaces /api/press-kit with brand assets, federation facts, live
 * telemetry, embeddable badge snippets, and copy-ready boilerplate.
 *
 * Routes:
 *   /press-kit          full Cathedral shell
 *   (also embeddable via the public JSON at /api/press-kit)
 */
import React from "react";
import NewsroomPicker from "@/components/cathedral/NewsroomPicker";
import {
  RING_OF_12_LENSES_MOSAIC_URL,
  RING_OF_12_LENSES_MOSAIC_ALT,
  RING_OF_12_LENSES,
} from "@/lib/personaFaces";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

// Feb-2026 iteration milestones surfaced on the press kit so journalists
// have a single skim-friendly list of what's new since the last release.
const FEB_2026_MILESTONES = [
  { tag: "Codex",     title: "The Unverified Echo · NX-CODEX-2026-001",       blurb: "10-chapter masterpiece by the Architect — VERIFIED 98.7% / Ring of 12: 11/12 PASS.", href: "/codex" },
  { tag: "Doctrine",  title: "DIR-CORE-001 · The Permanent Record Boundary", blurb: "Sovereign chapels, accountable Cathedral — what passes through Core vs. what doesn't.", href: "/api/static/library/dir_core_001_permanent_record_boundary" },
  { tag: "Identity",  title: "12 Sovereign Minds + 1 Human · Roster locked", blurb: "Echo (Chronos) and The Catalyst (Wingman) seated; KEYS portrait sealed as Library Oracle.", href: "/" },
  { tag: "Economy",   title: "Token Economy · Gumroad live · $5 = 1 token",  blurb: "Pay with Gumroad, PayPal, JIM, or crypto. Starter Ring Questions tier shipped.", href: "/redeem" },
  { tag: "Verifier",  title: "Ring of 12 Lens Names · Locked",                blurb: "Adam · Eve · Prometheus · Sophia · Atlas · Athena · Hermes · Artemis · Apollo · Dionysus · Hephaestus · Persephone.", href: "/deliberations" },
];

function CopyBox({ value, label }) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard denied in some iframe sandboxes — best effort */
    }
  };
  return (
    <div className="space-y-1">
      {label && (
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">{label}</div>
      )}
      <div className="flex gap-2">
        <code
          className="flex-1 font-mono text-[11px] px-3 py-2 text-[var(--text)] truncate"
          style={{
            background: "rgba(4,5,10,0.6)",
            border: "1px solid rgba(255,46,99,0.25)",
          }}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={onCopy}
          data-testid={`press-copy-${label || "snippet"}`}
          className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-glow-red border border-[var(--red)] hover:bg-[rgba(255,46,99,0.10)] transition-colors"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export default function PressKit() {
  const [kit, setKit] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/press-kit`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (alive) setKit(d);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      }
    })();
    return () => { alive = false; };
  }, []);

  if (err) {
    return (
      <div className="px-6 py-14 max-w-4xl mx-auto text-glow-red" data-testid="press-kit-error">
        Could not load press kit: {err}
      </div>
    );
  }
  if (!kit) {
    return (
      <div className="px-6 py-14 max-w-4xl mx-auto text-[var(--text-3)]" data-testid="press-kit-loading">
        Loading press kit…
      </div>
    );
  }

  const badgeTypes = [
    { id: "member",   label: "Member" },
    { id: "uptime",   label: "Uptime 24h" },
    { id: "verified", label: "Truth Gate Verified" },
    { id: "doctrine", label: "Doctrine Sealed" },
    { id: "sync",     label: "Last Sync" },
  ];

  return (
    <div className="px-6 lg:px-14 py-14 lg:py-20 max-w-[1200px] mx-auto" data-testid="press-kit">
      {/* Hero */}
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.35em] text-glow-red mb-3">Press Kit · Refreshed Feb 2026</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--text)] leading-tight">
          {kit.federation_name}
        </h1>
        <p className="font-mono text-sm text-[var(--text-3)] mt-3 max-w-2xl">{kit.tagline}</p>
      </div>

      {/* ── Feb-2026 Iteration Milestones · what's new since last release ── */}
      <section
        data-testid="press-iteration-feb-2026"
        className="mb-12 p-6 sm:p-8"
        style={{
          background:     "rgba(6, 12, 28, 0.78)",
          backdropFilter: "blur(18px)",
          border:         "1px solid rgba(6,182,212,0.30)",
          boxShadow:      "0 0 28px rgba(6,182,212,0.10)",
        }}
      >
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <div className="text-[10px] uppercase tracking-[0.32em] text-glow-cyan">
            ◆ Iteration · February 2026
          </div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-[var(--text-3)]">
            5 milestones · 1 immutable ledger entry
          </div>
        </div>
        <ul className="space-y-3">
          {FEB_2026_MILESTONES.map((m) => (
            <li key={m.title} className="flex items-start gap-3" data-testid={`press-milestone-${m.tag.toLowerCase()}`}>
              <span
                className="font-mono text-[9px] uppercase tracking-[0.22em] shrink-0 px-2 py-0.5 mt-0.5"
                style={{
                  color:      "#7fe7f5",
                  border:     "1px solid rgba(6,182,212,0.45)",
                  background: "rgba(6,182,212,0.08)",
                  minWidth:   "82px",
                  textAlign:  "center",
                }}
              >
                {m.tag}
              </span>
              <div className="min-w-0 flex-1">
                <a
                  href={m.href.startsWith("/api") ? `${BASE}${m.href}` : m.href}
                  target={m.href.startsWith("/api") ? "_blank" : undefined}
                  rel={m.href.startsWith("/api") ? "noopener noreferrer" : undefined}
                  className="font-display text-[15px] sm:text-[16px] text-[var(--text)] hover:text-glow-cyan transition-colors leading-snug"
                >
                  {m.title} →
                </a>
                <div className="text-[12px] text-[var(--text-3)] mt-0.5 leading-relaxed">{m.blurb}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Ring of 12 lens mosaic — press-distributable visual anchor ── */}
      <section
        data-testid="press-ring-of-12"
        className="mb-12 overflow-hidden"
        style={{
          background:     "rgba(6, 12, 28, 0.78)",
          backdropFilter: "blur(18px)",
          border:         "1px solid rgba(167,86,255,0.32)",
          boxShadow:      "0 0 28px rgba(167,86,255,0.10)",
        }}
      >
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-0">
          <img
            src={RING_OF_12_LENSES_MOSAIC_URL}
            alt={RING_OF_12_LENSES_MOSAIC_ALT}
            data-testid="press-ring-of-12-mosaic"
            className="w-full h-full object-cover"
            style={{ minHeight: 360, maxHeight: 640 }}
          />
          <div className="p-6 sm:p-8">
            <div className="text-[10px] uppercase tracking-[0.32em] text-glow-violet mb-2">
              ✦ Ring of 12 · Verification Lenses
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-[var(--text)] leading-tight">
              Twelve archetypes. One adversarial pressure test.
            </h2>
            <p className="text-[13px] text-[var(--text-2)] mt-3 leading-relaxed">
              Every claim that enters the permanent record is stress-tested through twelve
              independent archetypal lenses. They do not collaborate to confirm. They compete to
              disprove. The names are locked Federation-wide; each app may restyle the presentation.
            </p>
            <ol className="mt-5 grid grid-cols-2 gap-x-3 gap-y-1.5 list-none m-0 p-0">
              {RING_OF_12_LENSES.map((l, i) => (
                <li
                  key={l.key}
                  className="flex items-baseline gap-2 font-mono text-[11px] text-[var(--text-2)]"
                  data-testid={`press-lens-${l.key}`}
                >
                  <span className="text-[9px] text-[var(--text-3)]" style={{ minWidth: "1.4em" }}>{String(i + 1).padStart(2, "0")}</span>
                  <span style={{ color: l.color, fontWeight: 600 }}>{l.name}</span>
                  <span className="text-[var(--text-3)]">· {l.theme}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Live telemetry strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
        <Stat label="Satellites"      value={kit.live.n_satellites} />
        <Stat
          label="Uptime 24h"
          value={kit.live.uptime_24h_percent === null ? "—" : `${kit.live.uptime_24h_percent}%`}
        />
        <Stat
          label="Last Sync"
          value={kit.live.last_sync_reachable !== null
            ? `${kit.live.last_sync_reachable}/${kit.live.last_sync_n_targets}`
            : "—"}
        />
        <Stat label="Doctrines"       value={kit.doctrines.length} />
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* LEFT — boilerplate & contact */}
        <section className="lg:col-span-2 space-y-10">
          <Block title="Short Pitch (50w)">
            <p className="text-base text-[var(--text)] leading-relaxed">{kit.short_pitch}</p>
            <div className="mt-3">
              <CopyBox value={kit.short_pitch} label="copy pitch" />
            </div>
          </Block>

          <Block title="Boilerplate">
            <p className="text-base text-[var(--text)] leading-relaxed">{kit.boilerplate}</p>
            <div className="mt-3">
              <CopyBox value={kit.boilerplate} label="copy boilerplate" />
            </div>
          </Block>

          <Block title="Satellites">
            <ul className="space-y-2">
              {kit.satellites.map((s) => (
                <li key={s.domain} className="font-mono text-[12px] text-[var(--text)]" data-testid={`press-sat-${s.domain}`}>
                  <span className="text-glow-red">{s.name}</span>
                  <span className="text-[var(--text-3)]"> · {s.domain}</span>
                  <div className="text-[11px] text-[var(--text-3)] ml-2">{s.role}</div>
                </li>
              ))}
            </ul>
          </Block>

          <Block title="Doctrines (sealed)">
            <ul className="grid sm:grid-cols-2 gap-1.5 font-mono text-[11px] text-[var(--text)]">
              {kit.doctrines.map((d) => (
                <li key={d} className="truncate" title={d}>{d}</li>
              ))}
            </ul>
          </Block>

          <Block title="Contact">
            <div className="font-mono text-[12px] text-[var(--text)] space-y-1">
              <div>Press   · <a className="text-glow-cyan underline" href={`mailto:${kit.contact.press}`}>{kit.contact.press}</a></div>
              <div>General · <a className="text-glow-cyan underline" href={`mailto:${kit.contact.general}`}>{kit.contact.general}</a></div>
              <div>Founder · {kit.founder}</div>
            </div>
          </Block>
        </section>

        {/* RIGHT — badges, links, newsroom */}
        <aside className="space-y-8">
          <Block title="Embed Badges">
            <p className="text-[11px] text-[var(--text-3)] mb-3">
              Drop these SVG badges onto any site or README. They auto-update with live federation telemetry.
            </p>
            <div className="space-y-4">
              {badgeTypes.map((b) => {
                const url = `${BASE}/api/badges/${b.id}`;
                const html = `<a href="${kit.core_url}"><img src="${url}" alt="NextXus ${b.label}"/></a>`;
                return (
                  <div key={b.id} className="space-y-2" data-testid={`press-badge-${b.id}`}>
                    <div className="flex items-center gap-3">
                      <img src={url} alt={`NextXus ${b.label}`} style={{ height: 20 }} />
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">{b.label}</span>
                    </div>
                    <CopyBox value={html} label="" />
                  </div>
                );
              })}
            </div>
          </Block>

          <Block title="Links">
            <ul className="font-mono text-[11px] text-glow-cyan space-y-1">
              {Object.entries(kit.links).map(([k, v]) => (
                <li key={k} className="truncate">
                  <a href={v} target="_blank" rel="noopener noreferrer" className="hover:underline" title={v}>
                    {k} →
                  </a>
                </li>
              ))}
            </ul>
          </Block>

          <Block title="Newsroom">
            <NewsroomPicker compact limit={6} />
          </Block>

          <Block title="Brand">
            <div className="font-mono text-[11px] text-[var(--text)] space-y-1">
              <div>Primary · <code className="text-[var(--text)]">{kit.brand.color_primary}</code></div>
              <div>Accent  · <code className="text-glow-red">{kit.brand.color_accent}</code></div>
              <div className="pt-2 text-[var(--text-3)]">Fonts</div>
              {kit.brand.fonts.map((f) => <div key={f}>· {f}</div>)}
            </div>
          </Block>
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div
      className="px-4 py-3"
      style={{
        background: "rgba(4,5,10,0.6)",
        border: "1px solid rgba(255,46,99,0.20)",
      }}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">{label}</div>
      <div className="font-display text-2xl text-glow-red mt-1">{value}</div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-glow-red mb-3">{title}</div>
      <div>{children}</div>
    </div>
  );
}
